import { MeetingInsights, SpeechSegment } from '../../../services/api.service';
import { AdvancedMeetingMetrics } from './advanced-metrics';
import {
  formatSpeakerFullName,
  getSegmentEndSec,
  getSegmentStartSec,
  getSpeakerName,
} from './audio-segments';

/**
 * Бенчмарк-инсайты: «заголовочные» фразы-сравнения с нормой
 * и пороговые зоны «хорошо / средне / плохо» для метрик встречи.
 * Только чистая логика — тексты фраз лежат в i18n-блоке "benchmark".
 */

export type BenchmarkTone = 'ok' | 'warning' | 'alert';

export type BenchmarkPhrase = {
  id: string;
  /** Ключ внутри i18n-блока "benchmark" (без префикса) */
  textKey: string;
  params: Record<string, string | number>;
  tone: BenchmarkTone;
  /** «Острота» — для сортировки, больше = выше в списке */
  severity: number;
};

export type MetricZone = 'good' | 'medium' | 'bad';

export type ZonedAssessment = {
  zone: MetricZone;
  insight: string;
};

const MAX_PHRASES = 5;

/** Доля топ-спикера, выше которой встреча считается «захваченной» одним голосом. */
const DOMINANCE_SHARE_PCT = 50;
/** Монолог длиннее этого — слушатели теряют нить. */
const MONOLOGUE_ALERT_SEC = 60;
/** Вопросов на 10 минут: ниже — вялый монолог, выше — живая дискуссия. */
const QUESTIONS_PER_10MIN_LOW = 1.5;
const QUESTIONS_PER_10MIN_HIGH = 4;

// ---------------------------------------------------------------------------
// Пороговые зоны для scorecard-метрик
// ---------------------------------------------------------------------------

export function assessGini(gini: number): ZonedAssessment {
  if (gini < 0.4) {
    return { zone: 'good', insight: 'здоровый баланс — норма до 0.4' };
  }
  if (gini <= 0.6) {
    return { zone: 'medium', insight: 'заметный перекос — норма до 0.4' };
  }
  return { zone: 'bad', insight: 'один голос забирает встречу' };
}

export function assessQuestionRate(ratePct: number): ZonedAssessment {
  if (ratePct >= 15) {
    return { zone: 'good', insight: 'живой диалог — вопросов достаточно' };
  }
  if (ratePct >= 7) {
    return { zone: 'medium', insight: 'вопросов маловато — норма от 15%' };
  }
  return { zone: 'bad', insight: 'почти нет вопросов — монологный формат' };
}

export function assessWpm(wpm: number): ZonedAssessment {
  if (wpm >= 100 && wpm <= 160) {
    return { zone: 'good', insight: 'комфортный темп — норма 100–160' };
  }
  if ((wpm >= 80 && wpm < 100) || (wpm > 160 && wpm <= 190)) {
    return { zone: 'medium', insight: 'темп на границе нормы 100–160' };
  }
  return {
    zone: 'bad',
    insight: wpm < 80 ? 'слишком медленно — норма 100–160' : 'слишком быстро — норма 100–160',
  };
}

export function assessOverlaps(total: number, durationSec: number): ZonedAssessment {
  const per10Min = durationSec > 0 ? total / (durationSec / 600) : total;
  if (per10Min < 3) {
    return { zone: 'good', insight: 'участники слушают друг друга' };
  }
  if (per10Min <= 6) {
    return { zone: 'medium', insight: 'периодически говорят одновременно' };
  }
  return { zone: 'bad', insight: 'часто говорят одновременно' };
}

// ---------------------------------------------------------------------------
// Бенчмарк-фразы
// ---------------------------------------------------------------------------

type SpeakerShare = {
  name: string;
  seconds: number;
  sharePct: number;
};

function computeSpeakerShares(segments: SpeechSegment[]): SpeakerShare[] {
  const secByName = new Map<string, number>();
  segments.forEach(seg => {
    const dur = Math.max(0, getSegmentEndSec(seg) - getSegmentStartSec(seg));
    if (dur <= 0) return;
    const name = getSpeakerName(seg);
    secByName.set(name, (secByName.get(name) || 0) + dur);
  });

  const total = Array.from(secByName.values()).reduce((a, b) => a + b, 0);
  if (total <= 0) return [];

  return Array.from(secByName.entries())
    .map(([name, seconds]) => ({
      name,
      seconds,
      sharePct: Math.round((seconds / total) * 100),
    }))
    .sort((a, b) => b.seconds - a.seconds);
}

function countSilentSpeakers(
  segments: SpeechSegment[],
  speakerTimelines: { name: string }[],
): { silent: number; total: number } {
  const spoke = new Set<string>();
  segments.forEach(seg => {
    if ((seg.text || '').trim()) spoke.add(getSpeakerName(seg));
  });
  const total = speakerTimelines.length;
  const silent = speakerTimelines.filter(s => !spoke.has(s.name)).length;
  return { silent, total };
}

function formatMinSec(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Прогнал ли ИИ-анализ (иначе «0 решений» — просто отсутствие данных). */
function aiAnalysisRan(insights: MeetingInsights | null): boolean {
  if (!insights) return false;
  return (
    (insights.tone_segments?.length ?? 0) > 0
    || (insights.key_topics?.length ?? 0) > 0
    || (insights.key_moments?.length ?? 0) > 0
  );
}

export function buildBenchmarkPhrases(params: {
  segments: SpeechSegment[];
  speakerTimelines: { name: string; color: string }[];
  durationSec: number;
  metrics: AdvancedMeetingMetrics | null;
  insights: MeetingInsights | null;
  maxPhrases?: number;
}): BenchmarkPhrase[] {
  const {
    segments,
    speakerTimelines,
    durationSec,
    metrics,
    insights,
    maxPhrases = MAX_PHRASES,
  } = params;

  if (!segments.length || durationSec <= 0) return [];

  const phrases: BenchmarkPhrase[] = [];
  const durationMin = Math.max(1, Math.round(durationSec / 60));

  // 1. Доминирование топ-спикера (> 50% времени речи)
  const shares = computeSpeakerShares(segments);
  const top = shares[0];
  const dominanceFired = Boolean(top && top.sharePct > DOMINANCE_SHARE_PCT && shares.length > 1);
  if (top && dominanceFired) {
    phrases.push({
      id: 'dominance',
      textKey: 'phrase-dominance',
      params: { name: formatSpeakerFullName(top.name), pct: top.sharePct },
      tone: 'warning',
      severity: 90,
    });
  }

  // 2. Монолог длиннее минуты
  const longestMonologue = metrics?.monologues?.[0];
  if (longestMonologue && longestMonologue.durationSec > MONOLOGUE_ALERT_SEC) {
    phrases.push({
      id: 'monologue',
      textKey: 'phrase-monologue',
      params: {
        name: formatSpeakerFullName(longestMonologue.speaker),
        duration: formatMinSec(longestMonologue.durationSec),
      },
      tone: 'warning',
      severity: 75,
    });
  }

  // 3. Молчуны: участники без единой реплики
  const { silent, total } = countSilentSpeakers(segments, speakerTimelines);
  if (silent > 0 && total >= 2) {
    phrases.push({
      id: 'silent',
      textKey: 'phrase-silent',
      params: { silent, total },
      tone: 'warning',
      severity: 80,
    });
  }

  // 4. Вопросы: явная низкая или высокая вовлечённость (середину не выводим)
  if (metrics && durationSec >= 600) {
    const questionsPer10Min = metrics.questions.total / (durationSec / 600);
    if (questionsPer10Min < QUESTIONS_PER_10MIN_LOW) {
      phrases.push({
        id: 'questions-low',
        textKey: 'phrase-questions-low',
        params: { n: metrics.questions.total, minutes: durationMin },
        tone: 'warning',
        severity: 70,
      });
    } else if (questionsPer10Min >= QUESTIONS_PER_10MIN_HIGH) {
      phrases.push({
        id: 'questions-high',
        textKey: 'phrase-questions-high',
        params: { n: metrics.questions.total, minutes: durationMin },
        tone: 'ok',
        severity: 40,
      });
    }
  }

  // 5. Баланс речи (Джини): только полюса, без дублирования фразы о доминировании
  if (metrics) {
    if (metrics.gini > 0.6 && !dominanceFired) {
      phrases.push({
        id: 'gini-dominant',
        textKey: 'phrase-gini-dominant',
        params: { gini: metrics.gini },
        tone: 'warning',
        severity: 65,
      });
    } else if (metrics.gini < 0.25 && shares.length > 1) {
      phrases.push({
        id: 'gini-even',
        textKey: 'phrase-gini-even',
        params: { gini: metrics.gini },
        tone: 'ok',
        severity: 30,
      });
    }
  }

  // 6. Ни решений, ни поручений (только если ИИ-анализ реально отработал)
  if (
    aiAnalysisRan(insights)
    && durationMin >= 15
    && (insights!.decisions?.length ?? 0) === 0
    && (insights!.action_items?.length ?? 0) === 0
  ) {
    phrases.push({
      id: 'no-outcomes',
      textKey: 'phrase-no-outcomes',
      params: { minutes: durationMin },
      tone: 'alert',
      severity: 100,
    });
  }

  return [...phrases]
    .sort((a, b) => b.severity - a.severity)
    .slice(0, maxPhrases);
}
